import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Sprout,
  LogOut,
  User as UserIcon,
  Calendar,
  MessagesSquare,
  Leaf,
  BookOpen,
  Store,
  Settings,
  Library,
  Menu,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
      isActive ? "bg-sage-500/15 text-sage-700" : "text-ink-700 hover:bg-cream-200"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-2xl px-4 py-3 font-body text-base font-semibold transition-colors ${
      isActive ? "bg-sage-500/15 text-sage-700" : "text-ink-700 hover:bg-cream-200"
    }`;

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/");
  };

  const NAV_ITEMS = [
    { to: "/forum", label: "Tartışmalar", icon: MessagesSquare },
    { to: "/yazilar", label: "Yazılar", icon: BookOpen },
    { to: "/events", label: "Etkinlikler", icon: Calendar },
    { to: "/magaza", label: "Mağaza", icon: Store },
    { to: "/kaynaklar", label: "Kaynaklar", icon: Library },
    { to: "/hakkimizda", label: "Hakkımızda", icon: Leaf },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-cream-300 bg-cream-100/85 backdrop-blur-md"
    >
      <div className="k-container flex items-center justify-between py-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-sage-500 text-cream-50">
            <Sprout className="h-5 w-5" />
          </span>
          <span className="font-display text-4xl leading-none text-ink-900">kabile</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/forum" className={linkClass} data-testid="nav-forum">
            <MessagesSquare className="h-4 w-4" /> Tartışmalar
          </NavLink>
          <NavLink to="/yazilar" className={linkClass} data-testid="nav-articles">
            <BookOpen className="h-4 w-4" /> Yazılar
          </NavLink>
          <NavLink to="/events" className={linkClass} data-testid="nav-events">
            <Calendar className="h-4 w-4" /> Etkinlikler
          </NavLink>
          <NavLink to="/magaza" className={linkClass} data-testid="nav-store">
            <Store className="h-4 w-4" /> Mağaza
          </NavLink>
          <NavLink to="/kaynaklar" className={linkClass} data-testid="nav-resources">
            <Library className="h-4 w-4" /> Kaynaklar
          </NavLink>
          <NavLink to="/hakkimizda" className={linkClass} data-testid="nav-about">
            <Leaf className="h-4 w-4" /> Hakkımızda
          </NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user?.role === "admin" && (
            <NavLink to="/admin/ayarlar" className={linkClass} data-testid="nav-admin-settings" title="Site Ayarları">
              <Settings className="h-4 w-4" />
              <span className="hidden lg:inline">Ayarlar</span>
            </NavLink>
          )}
          {user ? (
            <>
              <NavLink to="/profile" className={linkClass} data-testid="nav-profile">
                <UserIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{user.name.split(" ")[0]}</span>
              </NavLink>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="k-btn-ghost text-sm"
                title="Çıkış"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="k-btn-ghost text-sm">
                Giriş Yap
              </Link>
              <Link to="/register" data-testid="header-register-link" className="k-btn-primary text-sm py-2 px-5">
                Katıl
              </Link>
            </>
          )}
        </div>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              className="grid h-10 w-10 place-items-center rounded-full text-ink-900 hover:bg-cream-200 md:hidden"
              data-testid="mobile-menu-trigger"
              aria-label="Menüyü aç"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-4/5 max-w-xs border-l border-cream-300 bg-cream-50 p-0 sm:max-w-xs"
            data-testid="mobile-menu-panel"
          >
            <SheetTitle className="sr-only">Menü</SheetTitle>
            <div className="flex h-full flex-col overflow-y-auto p-6">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="mb-6 flex items-center gap-2"
                data-testid="mobile-brand-link"
              >
                <span className="grid h-9 w-9 place-items-center rounded-full bg-sage-500 text-cream-50">
                  <Sprout className="h-5 w-5" />
                </span>
                <span className="font-display text-3xl leading-none text-ink-900">kabile</span>
              </Link>

              <nav className="flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <SheetClose asChild key={item.to}>
                    <NavLink to={item.to} className={mobileLinkClass} data-testid={`mobile-nav-${item.to.replace("/", "")}`}>
                      <item.icon className="h-5 w-5" /> {item.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </nav>

              {user?.role === "admin" && (
                <>
                  <div className="my-4 h-px bg-cream-300" />
                  <SheetClose asChild>
                    <NavLink to="/admin/ayarlar" className={mobileLinkClass} data-testid="mobile-nav-admin-settings">
                      <Settings className="h-5 w-5" /> Ayarlar
                    </NavLink>
                  </SheetClose>
                </>
              )}

              <div className="my-4 h-px bg-cream-300" />

              {user ? (
                <div className="flex flex-col gap-1">
                  <SheetClose asChild>
                    <NavLink to="/profile" className={mobileLinkClass} data-testid="mobile-nav-profile">
                      <UserIcon className="h-5 w-5" /> {user.name.split(" ")[0]}
                    </NavLink>
                  </SheetClose>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout-btn"
                    className="flex items-center gap-3 rounded-2xl px-4 py-3 font-body text-base font-semibold text-destructive transition-colors hover:bg-cream-200"
                  >
                    <LogOut className="h-5 w-5" /> Çıkış yap
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <SheetClose asChild>
                    <Link to="/login" data-testid="mobile-header-login-link" className="k-btn-secondary w-full text-center">
                      Giriş Yap
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/register" data-testid="mobile-header-register-link" className="k-btn-primary w-full text-center">
                      Katıl
                    </Link>
                  </SheetClose>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
